/**
 * CampaignPointsPlot — a campaign's OWN solves as a clickable CL-vs-axis scatter (FXC-9009).
 *
 * The provenance companion to FillMap: where FillMap shows the box filling out
 * axis×axis, this plots the RESULT (CL) against one campaign range axis at a
 * time, and every landed dot is a door — click it and the caller resolves the
 * point's run node (run_path → node id via the run table) and selects it, so
 * the geometry re-rigs, the flow slice follows, and the workbench provenance
 * panel points at the actual run (the polar-click precedent, same seam).
 *
 * Pure and props-driven like FillMap: steps come from campaignReplaySteps, the
 * palette from the caller, and the click is a callback — no fetching in here.
 */
import { useMemo, useState } from "react";

import type { CampaignAxisSpec } from "../../../api/highliftCampaign";
import type { HighliftPalette } from "../foil/useHighliftTheme";
import { fillMapAxes } from "./FillMap";
import type { ReplayStep } from "./campaignMath";

const MONO = "'JetBrains Mono', 'SFMono-Regular', Menlo, monospace";

const W = 560;
const H = 170;
const PAD_L = 44;
const PAD_R = 10;
const PAD_T = 10;
const PAD_B = 24;

function frac(v: number, lo: number, hi: number): number {
  if (hi === lo) return 0.5;
  return Math.max(0, Math.min(1, (v - lo) / (hi - lo)));
}

/** Compact per-point tooltip: condition on the picked axis + CL + provenance hints. */
function stepTitle(step: ReplayStep, axisLabel: string, axisValue: number): string {
  const parts = [
    `${axisLabel} ${axisValue.toFixed(3)}`,
    step.CL != null ? `CL ${step.CL.toFixed(3)}` : "CL pending",
    step.cached ? "cache hit" : step.backend ?? "",
    step.runPath ? "click → run node" : "run node not registered yet",
  ];
  return parts.filter(Boolean).join(" · ");
}

export function CampaignPointsPlot({
  steps,
  upTo,
  axes,
  palette,
  onPickStep,
}: {
  steps: ReplayStep[];
  /** Replay cursor — only steps[0..upTo] exist; null/undefined shows all. */
  upTo?: number | null;
  axes: CampaignAxisSpec[];
  palette: HighliftPalette;
  /** Click a landed dot → the caller selects its run node (provenance). */
  onPickStep?: (step: ReplayStep) => void;
}) {
  const rangeAxes = useMemo(() => fillMapAxes(axes), [axes]);
  const [axisKey, setAxisKey] = useState<string | null>(null);
  const axis = rangeAxes.find((ax) => ax.canonical === axisKey) ?? rangeAxes[0] ?? null;

  const visible = upTo == null ? steps : steps.slice(0, Math.max(0, upTo + 1));
  // Only landed points with a CL plot — failed/pending points have no honest Y.
  const landed = visible.filter((s) => !s.failed && s.CL != null);

  // Y range over the WHOLE campaign (stable as a replay reveals points).
  const cls = steps.map((s) => s.CL).filter((v): v is number => v != null);
  const clLo = cls.length ? Math.min(...cls) : 0;
  const clHi = cls.length ? Math.max(...cls) : 1;

  if (!axis || cls.length === 0) return null;

  const px = (s: ReplayStep) => PAD_L + frac(axis.value(s), axis.min, axis.max) * (W - PAD_L - PAD_R);
  const py = (s: ReplayStep) => H - PAD_B - frac(s.CL!, clLo, clHi) * (H - PAD_T - PAD_B);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }} data-testid="campaign-points-plot">
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 9.5, fontFamily: MONO, color: palette.ink3 }}>CL vs</span>
        {rangeAxes.map((ax) => (
          <button
            key={ax.canonical}
            type="button"
            data-testid={`campaign-points-axis-${ax.canonical}`}
            data-on={ax.canonical === axis.canonical ? "1" : "0"}
            onClick={() => setAxisKey(ax.canonical)}
            style={{
              fontSize: 9.5,
              fontFamily: MONO,
              padding: "1px 6px",
              borderRadius: 4,
              border: `1px solid ${ax.canonical === axis.canonical ? palette.primary : palette.line}`,
              background: ax.canonical === axis.canonical ? palette.surface2 : "transparent",
              color: ax.canonical === axis.canonical ? palette.ink : palette.ink3,
              cursor: "pointer",
            }}
          >
            {ax.label}
          </button>
        ))}
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", background: palette.surface2, borderRadius: 6, border: `1px solid ${palette.line}` }}
      >
        {/* Y extremes so the CL scale is readable without a full axis. */}
        <text x={PAD_L - 5} y={PAD_T + 8} textAnchor="end" fontSize={8.5} fontFamily={MONO} fill={palette.ink3}>
          {clHi.toFixed(2)}
        </text>
        <text x={PAD_L - 5} y={H - PAD_B} textAnchor="end" fontSize={8.5} fontFamily={MONO} fill={palette.ink3}>
          {clLo.toFixed(2)}
        </text>
        <line x1={PAD_L} y1={H - PAD_B} x2={W - PAD_R} y2={H - PAD_B} stroke={palette.line} strokeWidth={1} />
        <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={H - PAD_B} stroke={palette.line} strokeWidth={1} />
        {landed.map((s, i) => {
          const clickable = Boolean(onPickStep && s.runPath);
          const isCurrent = upTo != null && steps[upTo] === s;
          return (
            <g
              key={i}
              data-testid="campaign-points-dot"
              onClick={clickable ? () => onPickStep!(s) : undefined}
              style={clickable ? { cursor: "pointer" } : undefined}
            >
              <title>{stepTitle(s, axis.label, axis.value(s))}</title>
              {s.cached ? (
                <circle cx={px(s)} cy={py(s)} r={3.6} fill="none" stroke={palette.blue} strokeWidth={1.5} />
              ) : (
                <circle cx={px(s)} cy={py(s)} r={3.6} fill={palette.blue} opacity={0.85} />
              )}
              {/* An invisible fat hit-target so 3.6px dots are actually clickable. */}
              {clickable ? <circle cx={px(s)} cy={py(s)} r={9} fill="transparent" /> : null}
              {isCurrent ? (
                <circle cx={px(s)} cy={py(s)} r={7} fill="none" stroke={palette.primary} strokeWidth={1.5} />
              ) : null}
            </g>
          );
        })}
        <text x={(PAD_L + W - PAD_R) / 2} y={H - 8} textAnchor="middle" fontSize={8.5} fontFamily={MONO} fill={palette.ink3}>
          {axis.label}
        </text>
      </svg>
      <div style={{ fontSize: 9.5, fontFamily: MONO, color: palette.ink3, display: "flex", gap: 10 }}>
        <span>{landed.length} solved points</span>
        <span style={{ marginLeft: "auto" }}>hollow = cached · click a point → its run</span>
      </div>
    </div>
  );
}
